import React, { useEffect, createRef, useState } from 'react';
import { styled } from '@compiled/react';
import ChangeIndicator from '@atlaskit/icon/glyph/hipchat/media-attachment-count';
import { Section, SkeletonItem } from '@atlaskit/side-navigation';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { PREMIUM_EDITION } from '@atlassian/jira-shared-types/src/edition.tsx';
import { ExpandableSubmenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/expandable-submenu-item/index.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { TitleHeader } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/title-header/main.tsx';
import { useServiceDeskContextResource } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/common/service-desk-context-resource/index.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { NestingMenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/nesting-menu-item/main.tsx';
import { shouldRenderNesting } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/should-render/index.tsx';
import {
	matchPartialUrlPathname,
	matchUrlPathname,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import AsyncConfigurationChecker from '@atlassian/jira-servicedesk-configuration-checker/src/ui/async.tsx';
import { useTranslationSummaries } from '@atlassian/jira-servicedesk-languages/src/services/translation-summaries/index.tsx';
import { USER_JSM_PROJECT_ROLE } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/servicedesk-context/constants.tsx';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useDlpEntitlement } from '@atlassian/jira-tenant-context-controller/src/components/dlp-entitlement/index.tsx';
import { JourneyNavItemWrapper } from '@atlassian/jira-servicedesk-journeys/src/common/ui/journey-nav-item-wrapper/index.tsx';
import { useSettingFeatures } from '../../../controllers/features/main.tsx';
import {
	PROJECT_SETTINGS_SHIFTER_WEIGHT,
	SERVICE_DESK_LEGACY_TICKET_TYPES,
	SETTINGS_IDS,
} from '../../constants.tsx';
import { useLegacySpaClickHandler } from '../../utils/index.tsx';
import { AsyncAutomationChangeboarding } from '../automation-changeboarding/index.tsx';
import { Automation } from '../automation/main.tsx';
import ProjectSettingsChangeBoarding from '../settings-changeboarding/index.tsx';
import { AccessMenu } from './access/index.tsx';
import { ChannelsMenu } from './channels/index.tsx';
import { IntegrationsMenu } from './integrations/index.tsx';
import { Language } from './language/main.tsx';
import messages from './messages.tsx';
import { NotificationsMenu } from './notifications/index.tsx';
import { RequestsManagementMenu } from './requests-management/index.tsx';
import { TicketTypesItem } from './ticket-types-item/main.tsx';
import type { SettingsClassicProps } from './types.tsx';

const calculateRequestManagementActive = (path: string) =>
	/jira\/servicedesk\/projects\/.+\/settings\/request-types/.test(path) ||
	/jira\/servicedesk\/projects\/.+\/settings\/forms/.test(path) ||
	/plugins\/servlet\/project-config\/.+\/workflows/.test(path) ||
	/plugins\/servlet\/project-config\/.+\/slas/.test(path) ||
	/plugins\/servlet\/project-config\/.+\/feedback-settings/.test(path) ||
	/plugins\/servlet\/project-config\/.+\/issuetypes/.test(path) ||
	/plugins\/servlet\/project-config\/.+\/issuelayout/.test(path) ||
	/plugins\/servlet\/project-config\/.+\/fields/.test(path) ||
	/jira\/servicedesk\/projects\/.+\/settings\/screens/.test(path);

const calculateAccessActive = (path: string) =>
	/plugins\/servlet\/project-config\/.+\/people/.test(path) ||
	/plugins\/servlet\/project-config\/.+\/permissions/.test(path) ||
	/jira\/servicedesk\/projects\/.+\/settings\/customer-permissions/.test(path) ||
	/plugins\/servlet\/project-config\/.+\/issue-security/.test(path);

const calculateChannelsActive = (path: string) =>
	/jira\/servicedesk\/projects\/.+\/settings\/portal-settings/.test(path) ||
	/jira\/servicedesk\/projects\/.+\/settings\/email/.test(path) ||
	/jira\/servicedesk\/projects\/.+\/settings\/chat\?placement=projectSettings/.test(path) ||
	/jira\/servicedesk\/projects\/.+\/settings\/confluence-kb/.test(path) ||
	/jira\/servicedesk\/projects\/.+\/settings\/virtual-agent/.test(path) ||
	/jira\/servicedesk\/projects\/.+\/settings\/external-links/.test(path) ||
	/secure\/(.+)Collector/.test(path) ||
	/jira\/servicedesk\/projects\/.+\/settings\/widget/.test(path);

const calculateNotificationsActive = (path: string) =>
	/jira\/servicedesk\/projects\/.+\/settings\/customer-notifications/.test(path) ||
	/plugins\/servlet\/project-config\/.+\/notifications/.test(path);

const calculateIntegrationsActive = (path: string) =>
	/secure\/ConfigureDevStatus\.jspa\?projectKey=.+/.test(path) ||
	/jira\/servicedesk\/projects\/.+\/settings\/apps/.test(path);

const calculateOperationsActive = (path: string) =>
	/jira\/servicedesk\/projects\/.+\/settings\/change-management/.test(path) ||
	/jira\/servicedesk\/projects\/.+\/settings\/incident-management/.test(path);

/**
 * @deprecated This is a Nav3 component. If you do any changes here, please apply and QA them at Nav4 component as well: src/packages/navigation-apps/sidebar-nav4/project-settings/jsm-cmp-project-settings
 */
export const SettingsClassic = ({
	ecosystemItems,
	projectId,
	projectKey,
	stack,
}: SettingsClassicProps) => {
	const { data: features } = useSettingFeatures();
	const { formatMessage } = useIntl();
	const { data: project } = useProject();
	const appPermissions = useApplicationPermissions();
	const appEditions = useAppEditions();
	const route = useCurrentRoute();
	const handleLegacySPAClick = useLegacySpaClickHandler();
	const [{ data: translations }, { fetchTranslationSummaries }] = useTranslationSummaries();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { userRole } = useServiceDeskContextResource().data || {};
	const hasDlpEnabled = useDlpEntitlement();

	const [accessActive] = useState<boolean>(calculateAccessActive(route.path));
	const [requestManagementActive] = useState<boolean>(calculateRequestManagementActive(route.path));
	const [channelsActive] = useState<boolean>(calculateChannelsActive(route.path));
	const [notificationsActive] = useState<boolean>(calculateNotificationsActive(route.path));
	const [operationsActive] = useState<boolean>(calculateOperationsActive(route.path));
	const [integrationsActive] = useState<boolean>(calculateIntegrationsActive(route.path));

	const getSpaUrlForClassicSettings = (subroute: string, optionalParams?: string) =>
		`/jira/servicedesk/projects/${projectKey}/settings/${subroute}${optionalParams || ''}`;

	useEffect(() => {
		if (project && userRole === USER_JSM_PROJECT_ROLE.AGENT_PROJECT_ADMIN_ROLE) {
			fetchTranslationSummaries(project.id, createAnalyticsEvent({}));
		}
	}, [createAnalyticsEvent, fetchTranslationSummaries, project, userRole]);

	if (features == null || projectId == null || projectKey == null) return <SkeletonItem />;

	const classicSettingsUrl = `/plugins/servlet/project-config/${projectKey}`;
	const overrides = {
		BackMenuItem: {
			href: classicSettingsUrl,
			// @ts-expect-error - TS7006 - Parameter 'ev' implicitly has an 'any' type.
			onClick: (ev) => ev.preventDefault(),
			title: formatMessage(messages.itemBack),
		},
	};

	const shouldShowOperations = features.hasJsdChangeManagement || features.hasJsdIncidentManagement;
	const shouldShowNotifications = features.hasNotifications || features.hasCustomerNotifications;

	const hasVersionsOrComponents = features.hasVersions || features.hasComponents;
	const hasPremiumServiceDeskLicense = appEditions.serviceDesk === PREMIUM_EDITION;

	return (
		<>
			<TitleHeader>{formatMessage(messages.settings)}</TitleHeader>
			{features.hasDetails && (
				<MenuItem
					analytics={{ itemId: SETTINGS_IDS.DETAILS }}
					href={getSpaUrlForClassicSettings('details')}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.details)}
				</MenuItem>
			)}
			{features.hasSummary && (
				<MenuItem
					analytics={{ itemId: SETTINGS_IDS.SUMMARY }}
					href={`/plugins/servlet/project-config/${projectKey}/summary`}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.summary)}
				</MenuItem>
			)}
			<MenuItem
				analytics={{ itemId: SETTINGS_IDS.ITSM_FEATURES }}
				href={getSpaUrlForClassicSettings('itsm-features')}
				selectedOn={matchUrlPathname()}
				ref={createRef()}
			>
				{formatMessage(messages.itsmFeatures)}
			</MenuItem>
			<ExpandableSubmenuItem
				analytics={{ itemId: 'access-group' }}
				menuItemLabel={formatMessage(messages.accessGroup)}
				forceExpand={accessActive}
				submenuId="access-group-settings"
			>
				<AccessMenu
					projectKey={projectKey}
					features={features}
					onHandleLegacySPAClick={handleLegacySPAClick}
				/>
			</ExpandableSubmenuItem>
			<ExpandableSubmenuItem
				analytics={{ itemId: 'requests-group' }}
				menuItemLabel={formatMessage(messages.requestsManagementGroup)}
				forceExpand={requestManagementActive}
				submenuId="requests-group-settings"
			>
				{shouldRenderNesting(features.hasRequestTypes, stack, SERVICE_DESK_LEGACY_TICKET_TYPES) &&
					(stack.length > 1 && stack[1] === SERVICE_DESK_LEGACY_TICKET_TYPES ? (
						<RequestTypesWrapper>
							<TicketTypesItem
								projectKey={projectKey}
								nestingId={SERVICE_DESK_LEGACY_TICKET_TYPES}
							/>
						</RequestTypesWrapper>
					) : (
						<TicketTypesItem projectKey={projectKey} nestingId={SERVICE_DESK_LEGACY_TICKET_TYPES} />
					))}
				<RequestsManagementMenu
					projectKey={projectKey}
					features={features}
					onHandleLegacySPAClick={handleLegacySPAClick}
				/>
			</ExpandableSubmenuItem>
			<Automation
				overrides={overrides}
				projectKey={projectKey}
				isSimplified={false}
				hasJiraAutomation={features.hasProjectAutomation}
			/>
			{
				// Config checker should only be used when the user is a JSD agent admin.
				userRole === USER_JSM_PROJECT_ROLE.AGENT_PROJECT_ADMIN_ROLE ? (
					<AsyncConfigurationChecker projectKey={projectKey} />
				) : null
			}
			{route && !__SERVER__ && (
				<AsyncAutomationChangeboarding
					isLegacyAutomation={route.name === 'legacy-jsd-jira-automation'}
				/>
			)}
			{fg('jsm-journey-builder') && (
				<JourneyNavItemWrapper>
					<MenuItem
						analytics={{
							itemId: SETTINGS_IDS.JOURNEYS,
						}}
						href={getSpaUrlForClassicSettings('journeys')}
						selectedOn={matchPartialUrlPathname()}
						ref={createRef()}
					>
						{formatMessage(messages.journeysNonFinal)}
					</MenuItem>
				</JourneyNavItemWrapper>
			)}
			{hasPremiumServiceDeskLicense && fg('playbooks-in-jsm') && (
				<MenuItem
					analytics={{
						itemId: SETTINGS_IDS.PLAYBOOKS,
					}}
					href={getSpaUrlForClassicSettings('playbooks')}
					selectedOn={matchPartialUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.playbooks)}
				</MenuItem>
			)}
			<ExpandableSubmenuItem
				analytics={{ itemId: 'channels-group' }}
				menuItemLabel={formatMessage(messages.channelsGroup)}
				forceExpand={channelsActive}
				submenuId="channels-group-settings"
			>
				<ChannelsMenu
					projectKey={projectKey}
					features={features}
					onHandleLegacySPAClick={handleLegacySPAClick}
				/>
			</ExpandableSubmenuItem>
			{shouldShowOperations && (
				<ExpandableSubmenuItem
					analytics={{ itemId: 'operations-group' }}
					menuItemLabel={formatMessage(messages.operationsGroup)}
					forceExpand={operationsActive}
					submenuId="operations-group-settings"
				>
					{features.hasJsdChangeManagement && appPermissions.hasServiceDeskAccess && (
						<MenuItem
							analytics={{ itemId: SETTINGS_IDS.JSD_CHANGE_MANAGEMENT }}
							href={getSpaUrlForClassicSettings('change-management')}
							selectedOn={matchUrlPathname()}
							ref={createRef()}
						>
							{formatMessage(messages.jsdChangeManagement)}
						</MenuItem>
					)}
					{features.hasJsdIncidentManagement && (
						<MenuItem
							analytics={{
								itemId: SETTINGS_IDS.JSD_INCIDENT_MANAGEMENT,
							}}
							href={getSpaUrlForClassicSettings('incident-management')}
							onClick={handleLegacySPAClick}
							selectedOn={matchUrlPathname()}
							ref={createRef()}
						>
							{formatMessage(messages.jsdIncidentManagement)}
						</MenuItem>
					)}
				</ExpandableSubmenuItem>
			)}
			{shouldShowNotifications && (
				<ExpandableSubmenuItem
					analytics={{ itemId: 'notifications-group' }}
					menuItemLabel={formatMessage(messages.notificationsGroup)}
					forceExpand={notificationsActive}
					submenuId="notifications-group-settings"
				>
					<NotificationsMenu
						projectKey={projectKey}
						features={features}
						onHandleLegacySPAClick={handleLegacySPAClick}
					/>
				</ExpandableSubmenuItem>
			)}
			{(!fg('jcs_master_flag') || features.hasLanguageSupport) && (
				<NestingMenuItem
					analytics={{ itemId: 'language-support' }}
					href={getSpaUrlForClassicSettings('language')}
					iconAfter={
						translations.isLanguageSupportHighlighted ? (
							// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
							<ChangeIndicator primaryColor={token('color.icon.warning')} label="" />
						) : null
					}
					id="languageSupport"
					overrides={overrides}
					shifter={{
						groupTitle: formatMessage(messages.title),
						context: projectKey,
						weight: PROJECT_SETTINGS_SHIFTER_WEIGHT,
					}}
					title={formatMessage(messages.languageSupport)}
					selectedOn={matchPartialUrlPathname()}
				>
					<Language />
				</NestingMenuItem>
			)}
			<ExpandableSubmenuItem
				analytics={{ itemId: 'integrations-group' }}
				menuItemLabel={formatMessage(messages.integrationsGroup)}
				forceExpand={integrationsActive}
				submenuId="integrations-group-settings"
			>
				<IntegrationsMenu
					ecosystemItems={ecosystemItems}
					projectKey={projectKey}
					features={features}
					onHandleLegacySPAClick={handleLegacySPAClick}
				/>
			</ExpandableSubmenuItem>
			{hasDlpEnabled && (
				<MenuItem
					analytics={{ itemId: SETTINGS_IDS.DATA_CLASSIFICATIONS }}
					href={`/plugins/servlet/project-config/${projectKey}/data-classifications`}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.dataClassificationsNonFinal)}
				</MenuItem>
			)}
			{hasVersionsOrComponents && (
				<Section>
					{features.hasVersions && (
						<MenuItem
							analytics={{
								itemId: SETTINGS_IDS.VERSIONS,
							}}
							href={`/plugins/servlet/project-config/${projectKey}/administer-versions`}
							selectedOn={matchUrlPathname()}
							ref={createRef()}
						>
							{formatMessage(messages.versions)}
						</MenuItem>
					)}
					{features.hasComponents && (
						<MenuItem
							analytics={{
								itemId: SETTINGS_IDS.COMPONENTS,
							}}
							href={`/plugins/servlet/project-config/${projectKey}/administer-components`}
							selectedOn={matchUrlPathname()}
							ref={createRef()}
						>
							{formatMessage(messages.components)}
						</MenuItem>
					)}
				</Section>
			)}
			<ProjectSettingsChangeBoarding />
		</>
	);
};

// Padding inconsistencies happen in L2 RT panel and it comes from ExpandableMenuItem used in new Nav3
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const RequestTypesWrapper = styled.div({
	// All spans on L2 panel except for Section title "WORK CATEGORIES"
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		paddingLeft: token('space.050'),
	},

	// All <a />s on L2 panel except for Section title "WORK CATEGORIES"
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	a: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		marginLeft: `${token('space.050')} !important`,
	},

	// Section title "WORK CATEGORIES"
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"& [role='heading']": {
		marginLeft: token('space.050'),
	},

	// Back to project settings button
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'& > div:first-child': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'a > span': {
			gap: token('space.050'),
			paddingLeft: '0px',
		},
	},
});
